import { Link } from "@tanstack/react-router";
import { Section } from "./Section";
import { RevealOnScroll } from "./RevealOnScroll";

export type RelatedChannelItem = {
  to: string;
  name: string;
  sub: string;
};

type Props = {
  locale: "de" | "en";
  items: RelatedChannelItem[];
  variant?: "default" | "tinted";
};

const labels = {
  de: {
    kicker: "Weitere Werbekanäle",
    headline: <>Andere Performance-Kanäle <em>im Zusammenspiel.</em></>,
    caption: "Verwandte Plattformen, die in vielen Setups parallel oder ergänzend laufen.",
    back: "← Alle Performance-Marketing-Disziplinen",
    backHref: "/leistungen/performance-marketing",
  },
  en: {
    kicker: "Other ad channels",
    headline: <>Adjacent performance channels <em>in interplay.</em></>,
    caption: "Related platforms that often run in parallel or complement each other.",
    back: "← All performance marketing disciplines",
    backHref: "/en/services/performance-marketing",
  },
} as const;

export function RelatedChannels({ locale, items, variant = "default" }: Props) {
  const L = labels[locale];
  return (
    <Section
      variant={variant}
      kicker={L.kicker}
      headline={L.headline}
      caption={L.caption}
    >
      <RevealOnScroll>
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]"
            >
              <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
              <span
                aria-hidden
                className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep"
              >
                →
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to={L.backHref}
            className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
          >
            {L.back}
          </Link>
        </div>
      </RevealOnScroll>
    </Section>
  );
}
